import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Camera, Trash2, Plus, Sparkles, Loader2, X } from "lucide-react";
import { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const readAsDataURL = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(r.result);
  r.onerror = reject;
  r.readAsDataURL(file);
});

export default function MenuScanDialog({ rid, onImported, trigger }) {
  const [open, setOpen] = useState(false);
  const [images, setImages] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [bands, setBands] = useState([]);
  const [targetBand, setTargetBand] = useState("BASE");

  useEffect(() => {
    if (open) api.get(`/restaurants/${rid}/price-bands`).then((r) => setBands(r.data)).catch(() => {});
  }, [open, rid]);

  const reset = () => { setImages([]); setResult(null); setScanning(false); setImporting(false); setTargetBand("BASE"); };

  const addFiles = async (files) => {
    const arr = Array.from(files || []).slice(0, 8);
    const urls = await Promise.all(arr.map(readAsDataURL));
    setImages((p) => [...p, ...urls].slice(0, 8));
  };

  const scan = async () => {
    if (images.length === 0) return toast.error("Add at least one menu photo");
    setScanning(true);
    try {
      const { data } = await api.post(`/restaurants/${rid}/menu/scan`, { images });
      setResult(data.categories);
      toast.success(`Read ${data.item_count} items — charged £${Number(data.charged).toFixed(2)} from your wallet`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not read the menu");
    } finally { setScanning(false); }
  };

  const setCatName = (ci, v) => setResult((r) => r.map((c, i) => i === ci ? { ...c, name: v } : c));
  const removeCat = (ci) => setResult((r) => r.filter((_, i) => i !== ci));
  const setItem = (ci, ii, k, v) => setResult((r) => r.map((c, i) => i === ci
    ? { ...c, items: c.items.map((it, j) => j === ii ? { ...it, [k]: v } : it) } : c));
  const removeItem = (ci, ii) => setResult((r) => r.map((c, i) => i === ci
    ? { ...c, items: c.items.filter((_, j) => j !== ii) } : c));

  const doImport = async () => {
    const payload = {
      target_band_id: targetBand === "BASE" ? null : targetBand,
      categories: (result || []).map((c) => ({
        name: c.name, items: c.items.map((it) => ({
          name: it.name, description: it.description || "", price: Number(it.price) || 0, subcategory: it.subcategory || "" })),
      })).filter((c) => c.name && c.items.length),
    };
    if (payload.categories.length === 0) return toast.error("Nothing to import");
    setImporting(true);
    try {
      const { data } = await api.post(`/restaurants/${rid}/menu/import`, payload);
      toast.success(`Added ${data.items_created} items in ${data.categories_created} new categories`);
      setOpen(false); reset(); onImported();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Import failed");
    } finally { setImporting(false); }
  };

  const totalItems = (result || []).reduce((s, c) => s + c.items.length, 0);

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="menu-scan-dialog">
        <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Sparkles className="h-5 w-5 text-primary" /> Import menu from photo</DialogTitle></DialogHeader>

        {!result && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">Snap a photo of your printed menu (add several photos for multi-page menus). Our AI reads it into an editable menu you can review before saving. A small wallet fee applies per photo.</p>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
              {images.map((src, i) => (
                <div key={i} className="relative rounded-lg overflow-hidden border border-border aspect-square" data-testid={`scan-photo-${i}`}>
                  <img src={src} alt="menu" className="w-full h-full object-cover" />
                  <button onClick={() => setImages((p) => p.filter((_, j) => j !== i))}
                    className="absolute top-1 right-1 h-6 w-6 rounded-full bg-black/60 text-white flex items-center justify-center"><X className="h-3.5 w-3.5" /></button>
                </div>
              ))}
              {images.length < 8 && (
                <label className="cursor-pointer aspect-square rounded-lg border-2 border-dashed border-border flex flex-col items-center justify-center text-muted-foreground hover:border-primary/50" data-testid="scan-add-photo">
                  <input type="file" accept="image/*" capture="environment" multiple className="hidden" onChange={(e) => addFiles(e.target.files)} />
                  <Camera className="h-6 w-6" /><span className="text-xs mt-1">Add photo</span>
                </label>
              )}
            </div>
            <Button className="w-full h-12 gap-2" disabled={scanning || images.length === 0} onClick={scan} data-testid="scan-menu-btn">
              {scanning ? <><Loader2 className="h-4 w-4 animate-spin" /> Reading menu…</> : <><Sparkles className="h-4 w-4" /> Scan {images.length || ""} photo{images.length === 1 ? "" : "s"}</>}
            </Button>
          </div>
        )}

        {result && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">Review & edit ({totalItems} items). Fix any misreads, then import.</p>
              <Button variant="ghost" size="sm" onClick={reset} data-testid="scan-rescan">Rescan</Button>
            </div>
            {result.map((c, ci) => (
              <div key={ci} className="rounded-xl border border-border p-3 space-y-2" data-testid={`scan-cat-${ci}`}>
                <div className="flex items-center gap-2">
                  <Input value={c.name} onChange={(e) => setCatName(ci, e.target.value)} className="h-9 font-semibold" data-testid={`scan-cat-name-${ci}`} />
                  <Button size="icon" variant="ghost" onClick={() => removeCat(ci)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                </div>
                {c.items.map((it, ii) => (
                  <div key={ii} className="flex items-center gap-2" data-testid={`scan-item-${ci}-${ii}`}>
                    <Input value={it.name} onChange={(e) => setItem(ci, ii, "name", e.target.value)} placeholder="Item name" className="h-9 flex-1" />
                    <div className="flex items-center gap-1">
                      <span className="text-sm text-muted-foreground">£</span>
                      <Input type="number" step="0.01" value={it.price} onChange={(e) => setItem(ci, ii, "price", e.target.value)} className="h-9 w-20" />
                    </div>
                    <Button size="icon" variant="ghost" onClick={() => removeItem(ci, ii)}><Trash2 className="h-4 w-4 text-red-400" /></Button>
                  </div>
                ))}
              </div>
            ))}
            {bands.length > 0 && (
              <div className="rounded-xl border border-border p-3" data-testid="scan-band-select">
                <label className="text-sm font-medium block mb-1.5">Apply these prices to</label>
                <Select value={targetBand} onValueChange={setTargetBand}>
                  <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="BASE">Base price (all channels)</SelectItem>
                    {bands.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1.5">Choose a band to load this menu's prices into that channel (e.g. a dine-in menu into your Dine-in band).</p>
              </div>
            )}
            <Button className="w-full h-12 gap-2" disabled={importing} onClick={doImport} data-testid="scan-import-btn">
              {importing ? <><Loader2 className="h-4 w-4 animate-spin" /> Importing…</> : <><Plus className="h-4 w-4" /> Add {totalItems} items to my menu</>}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
